/**
 * Contracts for the reusable grid filter-restore framework. Kept separate from the
 * registry/coordinator so both, plus every page, depend on one small type surface.
 */

/**
 * The minimal store shape the coordinator drives. Every standard per-page filter
 * store already implements this (single `filters` signal behind these methods).
 */
export interface RestorableFilterStore {
  /** Reset filter selections to their defaults (does not fetch). */
  reset(): void;
  /** Fetch the grid with the current filters. */
  apply(): void;
  /** Fetch on page entry (skips only while a request is already in flight). */
  loadOnEnter(): void;
}

/** Router nav-state bag (Angular types it loosely); pages read their own keys. */
export type NavState = Record<string, unknown> | undefined;

/**
 * Per-page configuration for {@link FilterRestoreCoordinator.restoreOnEnter}. The
 * framework owns the flag + branch selection + reset/load; these optional hooks carry
 * the page-specific prep (reseed Role, load option lists, apply a deep-link value).
 */
export interface FilterRestoreConfig {
  /** Page identity in the registry (use a FILTER_RESTORE_KEYS value). */
  key: string;
  /** The page's root-provided filter store. */
  store: RestorableFilterStore;
  /**
   * DEFAULT-entry prep, run AFTER `store.reset()`: reseed Role, load option lists,
   * apply a deep-link nav-state value. Return `false` to DEFER the load (e.g. first
   * visit where Role is not seeded yet — a bootstrap effect loads later).
   */
  onDefault?: (navState: NavState) => boolean | void;
  /** RESTORE-entry prep run before the reload (e.g. reload statutory-dependent lists). */
  onRestore?: () => void;
  /**
   * PRESERVE-entry prep (hydration re-mount) run before the reload; defaults to
   * `onRestore` when omitted. Return `false` to skip the reload (e.g. Role unset).
   */
  onPreserve?: () => boolean | void;
  /**
   * Returns true when this entry's nav state carries a fresh deep-link scope (e.g. a dashboard
   * stat card). Such an entry is a FRESH INTENT, not a return: the coordinator takes the DEFAULT
   * branch (reset + onDefault) even if an Action-column return flag happens to be pending, and
   * CONSUMES that pending flag anyway so it cannot hijack a LATER, unrelated visit. Omit for
   * pages with no such deep-link source — the return flag alone still governs the three-way
   * branch, unchanged from today. Do NOT implement this as `navState != null`: Angular puts
   * `{ navigationId }` into `extras.state` on browser back/forward, which would otherwise be
   * misread as a deep link. Key the predicate on the page's OWN nav-state payload instead (e.g.
   * a non-empty `status` array for a dashboard card).
   */
  isDeepLink?: (navState: NavState) => boolean;
}

/** Stable per-page keys — avoids typos across mark/consume sites. */
export const FILTER_RESTORE_KEYS = {
  dashboard:         'dashboard',
  workspace:         'workspace',
  reports:           'reports',
  licenseRenew:      'license-renew',
  licenseActivation: 'license-activation',
  critical:          'critical',
  criticalSubfolder: 'critical-subfolder',
  notifications:     'notifications',
  messageCenter:     'message-center',
} as const;

/** Union of the known page keys. */
export type FilterRestoreKey =
  (typeof FILTER_RESTORE_KEYS)[keyof typeof FILTER_RESTORE_KEYS];
